import React, { useRef, useState } from "react";
import SectionLabel from "../../components/SectionLabel";
import "./DocumentsPanel.css";

const KIND_LABELS = {
  ticket: "Passagem",
  boarding_pass: "Cartão de embarque",
  receipt: "Recibo",
  protocol: "Protocolo",
  email: "E-mail",
  chat_transcript: "Transcrição de chat",
  screenshot: "Print",
  other: "Outro",
};

const STATUS_LABELS = {
  pending: "Aguardando",
  processing: "Extraindo",
  completed: "Pronto",
  failed: "Falhou",
};

const KIND_OPTIONS = Object.entries(KIND_LABELS);

function csrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.content : "";
}

function formatBytes(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function DocumentsPanel({ publicCode, documents = [] }) {
  const createAction = `/cases/${publicCode}/documents`;
  const fileInputRef = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]);

  function handleFileChange(event) {
    setSelectedFiles(Array.from(event.target.files ?? []));
  }

  function openFilePicker() {
    fileInputRef.current?.click();
  }

  const fileLabel =
    selectedFiles.length === 0
      ? "Escolher arquivos"
      : selectedFiles.length === 1
        ? selectedFiles[0].name
        : `${selectedFiles.length} arquivos selecionados`;

  return (
    <section className="trunfo-docs" aria-label="Documentos do caso">
      <header className="trunfo-docs__header">
        <SectionLabel tone="mute">Documentos</SectionLabel>
        <p className="trunfo-docs__lede">
          Anexe passagens, cartões de embarque, recibos e protocolos. A extração começa sozinha.
        </p>
      </header>

      <form
        method="post"
        action={createAction}
        encType="multipart/form-data"
        className="trunfo-docs__form"
      >
        <input type="hidden" name="authenticity_token" value={csrfToken()} />

        <label className="trunfo-docs__field">
          <span className="trunfo-docs__field-label">Tipo do documento</span>
          <select name="document[kind]" defaultValue="ticket" className="trunfo-docs__select">
            {KIND_OPTIONS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>

        <div className="trunfo-docs__field trunfo-docs__field--file">
          <span className="trunfo-docs__field-label">Arquivos</span>
          <button
            type="button"
            onClick={openFilePicker}
            className="trunfo-docs__file-trigger"
            aria-label="Selecionar arquivos para anexar"
          >
            <span className="trunfo-docs__file-trigger-label">{fileLabel}</span>
            <span className="trunfo-docs__file-trigger-hint" aria-hidden="true">
              Procurar
            </span>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            name="document[files][]"
            accept=".pdf,.jpg,.jpeg,.png,.heic,.webp,application/pdf,image/*"
            multiple
            required
            onChange={handleFileChange}
            className="trunfo-docs__file-input"
            aria-hidden="true"
            tabIndex={-1}
          />
        </div>

        <button type="submit" className="trunfo-btn trunfo-btn--primary trunfo-docs__submit">
          <span aria-hidden="true" className="trunfo-btn__plus">
            +
          </span>
          Enviar documentos
        </button>

        <p className="trunfo-docs__form-hint">
          Você pode selecionar vários arquivos do mesmo tipo de uma vez.
        </p>
      </form>

      {documents.length === 0 ? (
        <p className="trunfo-docs__empty">Nenhum documento enviado ainda.</p>
      ) : (
        <ul className="trunfo-docs__list">
          {documents.map((doc) => (
            <DocumentRow key={doc.id} doc={doc} />
          ))}
        </ul>
      )}
    </section>
  );
}

function DocumentRow({ doc }) {
  return (
    <li className="trunfo-docs__row">
      <div className="trunfo-docs__row-main">
        <span className="trunfo-docs__kind">{KIND_LABELS[doc.kind] ?? doc.kind}</span>
        {doc.url ? (
          <a href={doc.url} target="_blank" rel="noreferrer" className="trunfo-docs__filename">
            {doc.filename ?? "arquivo"}
          </a>
        ) : (
          <span className="trunfo-docs__filename">{doc.filename ?? "arquivo"}</span>
        )}
        <span className="trunfo-docs__meta">{formatBytes(doc.byte_size)}</span>
      </div>

      <div className="trunfo-docs__row-side">
        <span
          className={`trunfo-docs__status trunfo-docs__status--${doc.ocr_status}`}
          aria-label={`Status da extração: ${STATUS_LABELS[doc.ocr_status] ?? doc.ocr_status}`}
        >
          {STATUS_LABELS[doc.ocr_status] ?? doc.ocr_status}
        </span>

        <form method="post" action={doc.delete_path} className="trunfo-docs__delete">
          <input type="hidden" name="_method" value="delete" />
          <input type="hidden" name="authenticity_token" value={csrfToken()} />
          <button type="submit" className="trunfo-docs__delete-btn" aria-label="Remover documento">
            Remover
          </button>
        </form>
      </div>
    </li>
  );
}
